





import React, { useState, useRef, useEffect } from 'react';
import styles from './SupportChatWidget.module.css';
import support from '../../images/support.png';
import { useDispatch, useSelector } from 'react-redux';
import { useSupportSocket } from '../../api/useSupportSocket';
import { getconversations, postchatimage } from '../../store/slices/supportchatSlice';
// import { linkifyText } from '../../utils/linkify';
import { SUPPORT_BASE_API_URL } from '../../config/config';



// ─── File Helper Utils ───────────────────────────────────────────────────────

const FILE_ICONS = {
  pdf: '📄', doc: '📝', docx: '📝', xls: '📊', xlsx: '📊',
  txt: '📃', zip: '🗜️', rar: '🗜️', mp4: '🎬', mp3: '🎵',
  default: '📎',
};

function getFileIcon(fileName = '') {
  const ext = fileName.split('.').pop().toLowerCase();
  return FILE_ICONS[ext] || FILE_ICONS.default;
}

function isImage(fileName = '') {
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(fileName);
}



function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}


function toDisplayTime(rawTs) {
  let ms = null;

  if (typeof rawTs === 'number') {
    ms = rawTs > 1e12 ? rawTs : rawTs * 1000;
  } else if (typeof rawTs === 'string' && rawTs.trim() !== '') {
    const parsed = new Date(rawTs).getTime();
    ms = isNaN(parsed) ? null : parsed;
  }

  const date = ms != null && !isNaN(ms) ? new Date(ms) : new Date();
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}




function getHttpBaseUrl() {
  if (!SUPPORT_BASE_API_URL) return '';
  return SUPPORT_BASE_API_URL.replace(/\/api\/?$/i, '');
}

function safeText(content, fileName) {
  if (!content) return fileName || '';

  if (typeof content === 'string' && content.startsWith('{')) {
    try {
      const parsed = JSON.parse(content);
      if (parsed.type === 'FILE' || parsed.fileName || parsed.fileUrl) {
        return {
          isFile: true,
          fileName: parsed.fileName,
          fileUrl: parsed.fileUrl,
          fileSize: parsed.fileSize,
          mimeType: parsed.mimeType || null,
        };
      }
      return parsed.content || content;
    } catch (e) { }
  }

  return content;
}


// ─── Linkify Helper (URL detection in chat text) ─────────────────────────────
// NOTE: Uses React.createElement instead of JSX so it works safely
// regardless of how this .js file's babel loader is configured.

const URL_REGEX = new RegExp(
  '(' +
  '(?:https?:\\/\\/)[^\\s<>"\']+' +
  '|' +
  '(?:www\\.)[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}(?:[^\\s<>"\']*)?' +
  '|' +
  '(?:[a-zA-Z0-9-]+\\.)+(?:com|net|org|io|in|co|dev|app|xyz|info|biz|me|ai|us|uk|ca|so|link|shop|store|tech|online|site|pro|edu|gov)(?:\\/[^\\s<>"\']*)?' +
  ')',
  'gi'
);

function trimTrailingPunctuation(url) {
  let trimmed = url;
  let removed = '';
  const openParens = (trimmed.match(/\(/g) || []).length;
  const closeParens = (trimmed.match(/\)/g) || []).length;

  while (trimmed.length > 0) {
    const lastChar = trimmed[trimmed.length - 1];
    if (')' === lastChar && closeParens > openParens) {
      trimmed = trimmed.slice(0, -1);
      removed = lastChar + removed;
      continue;
    }
    if (/[.,!?;:'"]/.test(lastChar)) {
      trimmed = trimmed.slice(0, -1);
      removed = lastChar + removed;
      continue;
    }
    break;
  }
  return { trimmed, removed };
}

function normalizeHref(url) {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function handleLinkClick(e, href) {
  e.preventDefault();
  e.stopPropagation();
  const newWindow = window.open(href, '_blank', 'noopener,noreferrer');
  if (!newWindow) {
    window.location.href = href;
  }
}

function linkifyText(text) {
  if (!text || typeof text !== 'string') return text;

  const matches = [...text.matchAll(URL_REGEX)];
  if (matches.length === 0) return text;

  const nodes = [];
  let lastIndex = 0;

  matches.forEach((match, i) => {
    const rawMatch = match[0];
    const startIndex = match.index;

    if (startIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, startIndex));
    }

    const { trimmed, removed } = trimTrailingPunctuation(rawMatch);

    if (trimmed) {
      const href = normalizeHref(trimmed);
      nodes.push(
        React.createElement(
          'a',
          {
            key: `link-${i}-${startIndex}`,
            href: href,
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
            onClick: function (e) { e.stopPropagation(); },
            style: {
              color: 'inherit',
              textDecoration: 'underline',
              wordBreak: 'break-all',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'rgba(59,130,246,0.25)',
              touchAction: 'manipulation',
            },
          },
          trimmed
        )
      );
    } else {
      nodes.push(rawMatch);
    }

    if (removed) nodes.push(removed);
    lastIndex = startIndex + rawMatch.length;
  });

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}



function FileBubble({ msg, isSent }) {
  let src = msg.mediaUrl || msg.fileUrl || msg.content || '';

  if (src.startsWith('{')) {
    try {
      const parsed = JSON.parse(src);
      src = parsed.fileUrl || parsed.mediaUrl || parsed.content || src;
    } catch (e) { }
  }

  if (src && src.startsWith('/')) {
    src = `${getHttpBaseUrl()}${src}`;
  } else if (src && !src.startsWith('http') && !src.startsWith('data:') && !src.startsWith('blob:')) {
    src = `${getHttpBaseUrl()}/${src}`;
  }

  const fileName = msg.fileName || 'file';

  const displaySrc = msg.fileData || src;

  const isImageFile =
    fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i) ||
    (displaySrc && displaySrc.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i));

  if (isImageFile && displaySrc) {
    return (
      <div className={`${styles.msgBubble} ${isSent ? styles.sent : styles.incoming} ${styles.imageBubble}`}>
        <div className={styles.imageContainer}>
          <img
            src={displaySrc}
            alt={fileName}
            className={styles.imagePreview}
            onLoad={() => {
             
              const el = document.querySelector(`.${styles.messagesArea}`);
              
            }}
            onClick={() => window.open(displaySrc, '_blank')}
            onError={(e) => {
              console.error('Image load failed:', displaySrc);
              e.target.style.display = 'none';
            }}
          />
          <button
            className={styles.downloadBtn}
            onClick={(e) => {
              e.stopPropagation();
              const link = document.createElement('a');
              link.href = displaySrc;
              link.download = fileName;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            title="Download"
          >
            ⬇
          </button>
        </div>
        <div className={styles.fileNameLabel}>{fileName}</div>
      </div>
    );
  }


  return (
    <div className={`${styles.msgBubble} ${isSent ? styles.sent : styles.incoming} ${styles.fileBubble}`}>
      <a
        href={src || '#'}
        download={fileName}
        target="_blank"
        rel="noreferrer"
        className={styles.fileDownloadLink}
      >
        <span className={styles.fileIconLarge}>{getFileIcon(fileName)}</span>
        <div className={styles.fileInfo}>
          <span className={styles.fileNameText}>{fileName}</span>
          {msg.fileSize && <span className={styles.fileSizeText}>{formatBytes(msg.fileSize)}</span>}
        </div>
        <span className={styles.downloadIcon}>⬇</span>
      </a>
    </div>
  );
}


function normalizeHistoryMsg(m, userId) {
  const isMine = m.senderId === Number(userId);
  const hasFileData = !!(m.fileUrl || m.mediaUrl || m.path || m.url);
  const isFile = m.type === 'FILE' && hasFileData;

  return {
    id: m.id,
    text: isFile ? '' : safeText(m.content || m.message, m.fileName),
    sender: isMine ? 'mine' : 'other',
    time: toDisplayTime(m.timestamp ?? m.createdAt),
    type: isFile ? 'FILE' : 'CHAT',
    fileName: m.fileName,
    fileUrl: m.fileUrl || m.path || m.url,
    mediaUrl: m.mediaUrl,
    fileSize: m.fileSize,
    fileType: m.fileType || (isFile ? (isImage(m.fileName || '') ? 'image' : 'file') : null),
  };
}


export default function SupportChatWidget({ open, onClose, onUnread }) {
  const [minimized, setMinimized] = useState(false);
  const minimizedRef = useRef(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [inputText, setInputText] = useState('');
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const lastSentTempId = useRef(null);


  const messagesAreaRef = useRef(null);
  const isNearBottomRef = useRef(true);

  const { userId, username } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const chat = useSelector((state) => state.support.data);
  const parentId = chat?.parentId || null;
  const { sendMessage, onMessage, isConnected } = useSupportSocket(userId, parentId);

  useEffect(() => {
    minimizedRef.current = minimized;
  }, [minimized]);

  useEffect(() => {
    if (userId) dispatch(getconversations(userId));
  }, [dispatch, userId]);


  useEffect(() => {
    if (!userId) return;

    const cleanup = onMessage((msg) => {

      if (msg.type === 'MESSAGE_DELETED') {
        const deletedIds = msg.messageIds
          ? msg.messageIds.map(String)
          : msg.messageId
            ? [String(msg.messageId)]
            : [];

        if (deletedIds.length > 0) {
          setMessages((prev) =>
            prev.filter((m) => !deletedIds.includes(String(m.id)))
          );
        }
        return;
      }

      if (msg.type === 'HISTORY') {
        const historyMsgs = (msg.messages || []).map((m) =>
          normalizeHistoryMsg(m, userId)
        );
        setMessages(historyMsgs);
        return;
      }

      if (
        (msg.type === 'CHAT' || msg.type === 'FILE') &&
        msg.senderId === Number(userId) &&
        msg.id
      ) {
        setMessages((prev) => {
          const idx = prev.findIndex(
            (m) => m.sender === 'mine' && String(m.id) === String(lastSentTempId.current)
          );
          if (idx === -1) return prev;
          const updated = [...prev];
          updated[idx] = { ...updated[idx], id: msg.id };
          lastSentTempId.current = null;
          return updated;
        });
        return;
      }

      if (msg.type === 'SUBSCRIBED') return;

      if (msg.type === 'CHAT' || msg.type === 'FILE') {
        const hasFileData = !!(msg.fileData || msg.fileUrl || msg.mediaUrl || msg.path || msg.url);
        const isFile = msg.type === 'FILE' && hasFileData;
        const newMsg = {
          id: msg.id || Date.now(),
          text: isFile ? '' : safeText(msg.content || msg.message, msg.fileName),
          sender: 'other',
          time: toDisplayTime(msg.timestamp),
          type: isFile ? 'FILE' : 'CHAT',
          fileName: msg.fileName,
          fileData: msg.fileData,
          fileUrl: msg.fileUrl || msg.path || msg.url,
          mediaUrl: msg.mediaUrl,
          fileSize: msg.fileSize,
          fileType: msg.fileType || (isFile ? (isImage(msg.fileName) ? 'image' : 'file') : null),
        };
        setMessages((prev) => [...prev, newMsg]);

        if (minimizedRef.current || !open) {
          setUnreadCount((prev) => {
            const newCount = prev + 1;
            onUnread?.(newCount);
            return newCount;
          });
        }
      }
    });

    return cleanup;
  }, [onMessage, userId, open]);

  useEffect(() => {
    if (open) {
      isNearBottomRef.current = true;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => scrollToBottom(false));
      });
    }
  }, [open]);

 
  useEffect(() => {
    if (messages.length === 0) return;
    if (isNearBottomRef.current) {
      requestAnimationFrame(() => scrollToBottom(false));
    }
  }, [messages.length]);


  useEffect(() => {
    const wrapper = messagesAreaRef.current;
    if (!wrapper) return;
    const ro = new ResizeObserver(() => {
      if (isNearBottomRef.current) scrollToBottom(false);
    });
    ro.observe(wrapper);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (open) {
      setUnreadCount(0);
      onUnread?.(0);
    }
  }, [open]);

  const MAX_FILE_SIZE_MB = 100; 

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      alert(`File too large. Maximum allowed size is ${MAX_FILE_SIZE_MB} MB.`);
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
    if (isImage(file.name)) {
      setFilePreviewUrl(URL.createObjectURL(file));
    } else {
      setFilePreviewUrl(null);
    }
  };

  const scrollToBottom = (smooth = false) => {
    const el = messagesAreaRef.current;
    if (!el) return;
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollTop = el.scrollHeight; 
    }
  };

  const handleScroll = () => {
    const el = messagesAreaRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const clearFile = () => {
    setSelectedFile(null);
    setFilePreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };


  const handleSendText = () => {
    if (!inputText.trim() || !isConnected) return;

    const payload = { type: 'CHAT', content: inputText.trim() };
    const success = sendMessage(payload);

    if (success) {
      const tempId = `temp-${Date.now()}`;
      lastSentTempId.current = tempId;

      setMessages((prev) => [
        ...prev,
        {
          id: tempId,
          text: inputText.trim(),
          sender: 'mine',
          time: toDisplayTime(Date.now()),
          type: 'CHAT',
        },
      ]);
      setInputText('');
    }
  };

  const handleSendFile = async () => {
    if (!selectedFile || !isConnected) return;

    setIsSending(true);
    try {
      const result = await dispatch(postchatimage({
        file: selectedFile,
        senderId: userId,
        receiverId: parentId,
      })).unwrap();

      const data = result?.data || result;
      const fileTypeCategory = isImage(selectedFile.name) ? 'image' : 'file';

      const tempId = `temp-file-${Date.now()}`;
      setMessages((prev) => [
        ...prev,
        {
          id: tempId,
          text: '',
          sender: 'mine',
          time: toDisplayTime(data.timestamp ?? Date.now()),
          type: 'FILE',
          fileName: data.fileName || selectedFile.name,
          fileUrl: data.fileUrl || data.mediaUrl,
          mediaUrl: data.mediaUrl || data.fileUrl,
          fileSize: data.fileSize || selectedFile.size,
          fileType: fileTypeCategory,
        },
      ]);
      clearFile();
    } catch (err) {
      console.error('[FILE SEND ERROR]', err);
      alert('File upload failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    if (selectedFile) await handleSendFile();
    if (inputText.trim()) handleSendText();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleToggleMinimize = () => {
    setMinimized((m) => {
      if (m) setUnreadCount(0);
      return !m;
    });
  };

  if (!open) return null;

  return (
    <div className={`${styles.popup} ${minimized ? styles.minimized : styles.full}`}>

      {/* Header */}
      <div className={styles.header} onClick={handleToggleMinimize}>
        <div className={styles.headerLeft}>
          <div className={styles.headerAvatar}>
            <img src={support} alt="Support" className={styles.headerAvatarImg} />
          </div>
          <div>
            <div className={styles.headerTitle}>Support</div>
            <div className={styles.headerSub}>
              {minimized ? 'Click to expand' : isConnected ? '🟢 Online' : '🔴 Connecting...'}
            </div>
          </div>
        </div>

        <div className={styles.headerActions}>
          {minimized && unreadCount > 0 && (
            <button
              className={styles.unreadBadge}
              onClick={(e) => { e.stopPropagation(); handleToggleMinimize(); }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </button>
          )}

          <button
            className={styles.headerBtn}
            onClick={(e) => { e.stopPropagation(); handleToggleMinimize(); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              {minimized ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
            </svg>
          </button>

          <button
            className={styles.headerBtn}
            onClick={(e) => { e.stopPropagation(); onClose(); }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body */}
      {!minimized && (
        <div className={styles.body}>
          <div
            className={styles.messagesArea}
            ref={messagesAreaRef}
            onScroll={handleScroll}
          >
            <div className={styles.dateDivider}><span>Today</span></div>

            {messages.length === 0 && (
              <div className={styles.emptyMsg}>No messages yet. Say hello! 👋</div>
            )}

            {messages.map((msg) => {
              const isSent = msg.sender === 'mine';
              return (
                <div key={msg.id} className={`${styles.msgRow} ${isSent ? styles.outgoing : ''}`}>
                  {isSent && <div className={styles.msgAvatar} />}
                  <div>
                    {isSent && <div className={styles.msgName}>{username || 'You'}</div>}

                    {msg.type === 'FILE' && (msg.fileData || msg.fileUrl || msg.mediaUrl || msg.content) ? (
                      <FileBubble msg={msg} isSent={isSent} />
                    ) : (
                        <div className={`${styles.msgBubble} ${isSent ? styles.sent : styles.incoming}`}>
                          {linkifyText(msg.text)}
                        </div>
                    )}

                    <div
                      className={`${styles.msgTime} ${isSent ? styles.timeRight : ''}`}
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: '#9aa0b4',
                        marginTop: 2,
                        textAlign: isSent ? 'right' : 'left',
                      }}
                    >
                      {msg.time || toDisplayTime(Date.now())}
                    </div>
                  </div>
                </div>

                
              );
            })}

            <div ref={messagesEndRef} />
          </div>

          {/* File Preview */}
          {selectedFile && (
            <div className={styles.filePreview}>
              {filePreviewUrl ? (
                <img src={filePreviewUrl} alt="preview" className={styles.filePreviewThumb} />
              ) : (
                <span className={styles.filePreviewIcon}>{getFileIcon(selectedFile.name)}</span>
              )}
              <div className={styles.filePreviewDetails}>
                <span className={styles.filePreviewName}>{selectedFile.name}</span>
                <span className={styles.filePreviewSize}>{formatBytes(selectedFile.size)}</span>
              </div>
              <button onClick={clearFile} className={styles.clearFileBtn}>×</button>
            </div>
          )}

          {/* Input Area */}
          <div className={styles.inputWrap}>
            <div className={styles.inputBar}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.mp4,.mp3,.webm,.wav"
                style={{ display: 'none' }}
              />

              <button className={styles.attachBtn} onClick={handleAttachClick} title="Attach file">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
              </button>

              <input
                className={styles.textInput}
                type="text"
                placeholder={selectedFile ? 'Add a caption (optional)…' : 'Type a message…'}
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isSending}
              />

              <button
                className={styles.sendBtn}
                onClick={handleSend}
                disabled={isSending || (!inputText.trim() && !selectedFile)}
              >
                {isSending ? '...' : '➤'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>


   
  );


  
}